import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { rememberReturnTo } from '@/utils/returnTo';
import { AuthProvider, useAuthContext } from '@/contexts/AuthContext';
import { nextOnboardingPath } from '@/utils/onboardingPath';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ControlsProvider, FloatingControls } from '@/components/ui/PageControls';
import ErrorBoundary from '@/components/ErrorBoundary';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ConfigHealthBanner from '@/components/ConfigHealthBanner';
import RateLimitNotice from '@/components/errors/RateLimitNotice';
import WarmingNotice from '@/components/errors/WarmingNotice';
import PendingDeletionScreen from '@/components/account/PendingDeletionScreen';
import LearnerLayout from '@/components/LearnerLayout';
import { getHostRole } from '@/utils/hostContext';
import { useRerenderOnLanguageChange } from '@/i18n/useRerenderOnLanguageChange';

// ── Lazy-loaded pages ──────────────────────────────────────────────────────────
// Onboarding
const WelcomePage                      = lazy(() => import('@/pages/onboarding/WelcomePage'));
const SignInPage                        = lazy(() => import('@/pages/onboarding/SignInPage'));
const SignUpPage                        = lazy(() => import('@/pages/onboarding/SignUpPage'));
const ChooseSpecialtyPage               = lazy(() => import('@/pages/onboarding/ChooseSpecialtyPage'));
const SpecialtyPage                     = lazy(() => import('@/pages/onboarding/SpecialtyPage'));
const SpecialtyRequestConfirmPage       = lazy(() => import('@/pages/onboarding/SpecialtyRequestConfirmPage'));
const ForgotPasswordPage                = lazy(() => import('@/pages/onboarding/ForgotPasswordPage'));
const AuthActionPage                    = lazy(() => import('@/pages/onboarding/AuthActionPage'));
const PasswordResetConfirmationPage     = lazy(() => import('@/pages/onboarding/PasswordResetConfirmationPage'));
const VerifyEmailPage                   = lazy(() => import('@/pages/onboarding/VerifyEmailPage'));
const ProfileSetupPage                  = lazy(() => import('@/pages/onboarding/ProfileSetupPage'));
const PlacementIntroPage                = lazy(() => import('@/pages/onboarding/PlacementIntroPage'));
const PlacementFlowPage                 = lazy(() => import('@/pages/placement/PlacementFlowPage'));
const LevelConfirmedPage                = lazy(() => import('@/pages/onboarding/LevelConfirmedPage'));
const TrialActivationPage               = lazy(() => import('@/pages/onboarding/TrialActivationPage'));
// Main app
const LessonHomePage        = lazy(() => import('@/pages/LessonHomePage'));
const LessonPage            = lazy(() => import('@/pages/LessonPage'));
const LessonCompletionPage  = lazy(() => import('@/pages/LessonCompletionPage'));
const ProfilePage           = lazy(() => import('@/pages/ProfilePage'));
const MessagesPage          = lazy(() => import('@/pages/MessagesPage'));
const SubscribePage         = lazy(() => import('@/pages/SubscribePage'));
const PaymentSuccessPage    = lazy(() => import('@/pages/PaymentSuccessPage'));
// Admin
const AdminLayout           = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminDashboard        = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminUsers            = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminContent          = lazy(() => import('@/pages/admin/AdminContent'));
const AdminSpecialties      = lazy(() => import('@/pages/admin/AdminSpecialties'));
const AdminLessonLoader     = lazy(() => import('@/pages/admin/AdminLessonLoader'));
const AdminLessonPublishing = lazy(() => import('@/pages/admin/AdminLessonPublishing'));
const AdminLessonPreview    = lazy(() => import('@/pages/admin/AdminLessonPreview'));
const PreviewExit           = lazy(() => import('@/pages/admin/AdminLessonPreview').then(m => ({ default: m.PreviewExit })));
const PreviewComplete       = lazy(() => import('@/pages/admin/AdminLessonPreview').then(m => ({ default: m.PreviewComplete })));
const AdminPayments         = lazy(() => import('@/pages/admin/AdminPayments'));
const AdminAuditLog         = lazy(() => import('@/pages/admin/AdminAuditLog'));
const AdminProblemReports   = lazy(() => import('@/pages/admin/AdminProblemReports'));
const AdminGatewayCredentials = lazy(() => import('@/pages/admin/AdminGatewayCredentials'));
const AdminTesterAccess = lazy(() => import('@/pages/admin/AdminTesterAccess'));
const AdminEmailSettings = lazy(() => import('@/pages/admin/AdminEmailSettings'));
const AdminDataRetention = lazy(() => import('@/pages/admin/AdminDataRetention'));
const AdminScheduledJobs = lazy(() => import('@/pages/admin/AdminScheduledJobs'));
const AdminUnservableLessons = lazy(() => import('@/pages/admin/AdminUnservableLessons'));
const AdminAnalytics = lazy(() => import('@/pages/admin/AdminAnalytics'));
const AdminSpecialtyRequests = lazy(() => import('@/pages/admin/AdminSpecialtyRequests'));
const AdminContentPages = lazy(() => import('@/pages/admin/AdminContentPages'));
const AdminSlickPayTest = lazy(() => import('@/pages/admin/AdminSlickPayTest'));
const AdminRoles            = lazy(() => import('@/pages/admin/AdminRoles'));
const AdminSubscriptions    = lazy(() => import('@/pages/admin/AdminSubscriptions'));
const AdminPricing          = lazy(() => import('@/pages/admin/AdminPricing'));
const AdminSignInPage       = lazy(() => import('@/pages/admin/AdminSignInPage'));
// Legacy (kept for backward compat during migration)

function FullPageSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-paper">
      <LoadingSpinner size="lg" />
    </div>
  );
}

/** Guards routes that require Firebase Auth. */
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthContext();
  const location = useLocation();
  if (loading) return <FullPageSpinner />;
  if (!user) {
    // Round 130 (R130-5): remember where an email's "Open in the app" button was going, so sign-in can return there
    rememberReturnTo(`${location.pathname}${location.search}`);
    return <Navigate to="/welcome" replace />;
  }
  return <>{children}</>;
}

/**
 * Round 141 - the onboarding steps after the profile (placement, its result, the trial) need a confirmed e-mail address: an
 * e-mail-and-password account that has not confirmed it is shown "Confirm your email" instead (the server refuses these with
 * 403 EMAIL_NOT_VERIFIED as well). Google accounts are always confirmed and pass straight through.
 */
function ConfirmedEmailRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthContext();
  return <AuthRoute>{user && !user.emailVerified ? <Navigate to="/onboarding/verify-email" replace /> : children}</AuthRoute>;
}

/**
 * Guards routes that require email verification AND a completed onboarding
 * sequence (profile -> placement -> trial activation).
 *
 * CO3 — before this, onboarding was enforced only by each onboarding page's
 * own navigate() call on success; nothing at the route level checked
 * whether any of it had actually happened. Any verified user whose browser
 * loaded '/' by a path other than the exact in-app click chain (a bookmark,
 * closing and reopening a tab, a fresh page load) landed directly on
 * /lessons with no profile/placement/subscription at all, and
 * LessonHomePage's own status fallback then showed a false "trial has
 * ended" screen to a learner who never onboarded — reproduced live, CN2.
 *
 * Checked in the same order onboarding actually happens, so a learner who
 * stalled at any step lands back exactly there, not at the beginning.
 */
function VerifiedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, profile, subscription } = useAuthContext();
  if (loading) return <FullPageSpinner />;
  const next = nextOnboardingPath(user, profile, subscription);
  if (next) return <Navigate to={next} replace />;
  return <>{children}</>;
}

/** Guards admin routes. */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthContext();
  if (loading) return <FullPageSpinner />;
  if (!user)   return <Navigate to="/admin/sign-in" replace />;
  return <>{children}</>;
}

// Round 100, D2 — host-based route gating. Firebase Hosting serves this
// SAME build to both emi.irtaki.net ('app') and admin.irtaki.net ('admin')
// — see firebase.json's two hosting targets and hostContext.ts's own
// comment for why a single build with a runtime host check, not two
// separate builds, is the right call here. Only ONE of these two route
// trees is ever included in <Routes> for a given page load — the other
// is never matched, never rendered, and (since every page below is
// React.lazy) never even fetched. AdminGuardInner (AdminLayout.tsx) is a
// second, independent authorization check on top of this — this function
// decides which UI a HOST gets, not who is allowed to use it.
function LearnerRoutes() {
  return (
    <Route element={<LearnerLayout />}>
      {/* ── Public / Onboarding ───────────────────────────────── */}
      <Route path="/welcome"                    element={<WelcomePage />} />
      <Route path="/sign-in"                    element={<SignInPage />} />
      <Route path="/sign-up"                    element={<SignUpPage />} />
      {/* Round 135 (R135-1): sign-up starts by choosing the specialty; its description pages and the visitor's request confirmation are public */}
      <Route path="/choose-specialty"           element={<ChooseSpecialtyPage />} />
      <Route path="/specialties/:fieldId"       element={<SpecialtyPage />} />
      <Route path="/specialty-request/confirm"  element={<SpecialtyRequestConfirmPage />} />
      <Route path="/forgot-password"            element={<ForgotPasswordPage />} />
      <Route path="/forgot-password/sent"       element={<PasswordResetConfirmationPage />} />
      {/* Round 139d: the link in the "choose a new password" e-mail - checks the code on load, uses it only on submit */}
      <Route path="/auth/action"               element={<AuthActionPage />} />
      <Route path="/onboarding/verify-email"    element={<AuthRoute><VerifyEmailPage /></AuthRoute>} />
      <Route path="/onboarding/profile"         element={<AuthRoute><ProfileSetupPage /></AuthRoute>} />
      <Route path="/onboarding/placement-intro" element={<ConfirmedEmailRoute><PlacementIntroPage /></ConfirmedEmailRoute>} />
      <Route path="/onboarding/placement"       element={<ConfirmedEmailRoute><PlacementFlowPage /></ConfirmedEmailRoute>} />
      <Route path="/onboarding/level-confirmed" element={<ConfirmedEmailRoute><LevelConfirmedPage /></ConfirmedEmailRoute>} />
      <Route path="/onboarding/trial"           element={<ConfirmedEmailRoute><TrialActivationPage /></ConfirmedEmailRoute>} />

      {/* ── Protected App ─────────────────────────────────────── */}
      <Route path="/lessons"               element={<VerifiedRoute><LessonHomePage /></VerifiedRoute>} />
      <Route path="/lessons/:lessonId"     element={<VerifiedRoute><LessonPage /></VerifiedRoute>} />
      <Route path="/lessons/:lessonId/complete" element={<VerifiedRoute><LessonCompletionPage /></VerifiedRoute>} />
      <Route path="/profile"               element={<VerifiedRoute><ProfilePage /></VerifiedRoute>} />
      {/* Round 129b: Messages is open to ANY signed-in learner, at any onboarding step - a support reply must be readable before placement is finished.
          Every other learner page stays behind VerifiedRoute. MessagesPage itself shows no navigation to those pages until setup is complete. */}
      <Route path="/messages"              element={<AuthRoute><MessagesPage /></AuthRoute>} />
      <Route path="/subscribe"             element={<VerifiedRoute><SubscribePage /></VerifiedRoute>} />
      <Route path="/payment-success"       element={<VerifiedRoute><PaymentSuccessPage /></VerifiedRoute>} />

      {/* ── Legacy ─────────────────────────────────────────────── */}
      <Route path="/"                      element={<Navigate to="/lessons" replace />} />
      <Route path="/login"                 element={<Navigate to="/sign-in" replace />} />
      <Route path="/register"              element={<Navigate to="/sign-up" replace />} />
      <Route path="/placement"             element={<Navigate to="/onboarding/placement" replace />} />

      {/* ── Fallback ───────────────────────────────────────────── */}
      <Route path="*"                      element={<Navigate to="/welcome" replace />} />
    </Route>
  );
}

function AdminRoutes() {
  return (
    <>
      {/* Always English, always LTR — outside LearnerLayout, so `dir`/`lang`
          can never be set here (see LearnerLayout.tsx and i18n/config.ts's
          admin-namespace exclusion). Round 100, D3 — admin's own sign-in,
          not the learner /sign-in (which doesn't even exist in this host's
          route tree at all). */}
      <Route path="/admin/sign-in" element={<AdminSignInPage />} />
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        {/* Round 59 — AdminContent renders its own nested <Routes>
            (index / :lessonId / new). A parent path without the
            trailing "*" only ever matches "/admin/content" exactly —
            react-router's own console warning names this precisely.
            Discovered live: "/admin/content/new" (the round's own
            Upload Content page) silently fell through to no route
            match at all rather than rendering AdminContentUpload,
            the exact reason a plain "+ New Lesson"/"Upload Content"
            click never worked. Pre-existing, not introduced this
            round — the nested :lessonId route was equally broken,
            just never exercised since the Lesson Library table has
            been empty (a separate bug, already flagged separately). */}
        <Route path="content/*" element={<AdminContent />} />
        <Route path="specialties" element={<AdminSpecialties />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="audit-log" element={<AdminAuditLog />} />
        <Route path="tickets" element={<AdminProblemReports />} />
        <Route path="gateways" element={<AdminGatewayCredentials />} />
        <Route path="testers" element={<AdminTesterAccess />} />
        <Route path="email-settings" element={<AdminEmailSettings />} />
        <Route path="data-retention" element={<AdminDataRetention />} />
        <Route path="scheduled-jobs" element={<AdminScheduledJobs />} />
        <Route path="unservable-lessons" element={<AdminUnservableLessons />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="specialty-requests" element={<AdminSpecialtyRequests />} />
        <Route path="content-pages" element={<AdminContentPages />} />
        <Route path="gateways/slickpay-test" element={<AdminSlickPayTest />} />
        <Route path="roles" element={<AdminRoles />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="pricing" element={<AdminPricing />} />
        <Route path="lesson-loader" element={<AdminLessonLoader />} />
        <Route path="lesson-publishing" element={<AdminLessonPublishing />} />
        <Route path="preview/:lessonId" element={<AdminLessonPreview />} />
      </Route>
      {/* Round 116 - the learner player's own exits, so "Preview as learner" can end without leaving the admin app */}
      <Route path="/lessons" element={<AdminRoute><PreviewExit /></AdminRoute>} />
      <Route path="/lessons/:lessonId/complete" element={<AdminRoute><PreviewComplete /></AdminRoute>} />
      {/* Any non-admin path on the admin host lands on the dashboard (which
          itself redirects to /admin/sign-in if unauthenticated) rather than
          a dead end — there is no learner UI to fall back to on this host. */}
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </>
  );
}

export default function App() {
  const hostRole = getHostRole();
  // Re-render (never remount) the whole tree when the language changes, so
  // plain t() calls repaint while form state typed so far survives. See
  // i18n/useRerenderOnLanguageChange.ts and LearnerLayout.tsx.
  useRerenderOnLanguageChange();
  return (
    <ThemeProvider>
    {/* Round 145: every screen has the theme switch - a page's own PageControls, else the learner corner fallback (LearnerLayout), else this one
        (admin sign-in and two-step screens, the error and pending-deletion screens, the loading screen) */}
    <ControlsProvider>
    <FloatingControls level="root" />
    <ConfigHealthBanner />
    <RateLimitNotice />
    <WarmingNotice />
    <ErrorBoundary>
      <AuthProvider>
        {/* Round 139: a learner whose account is scheduled for deletion sees "Keep my account" instead of the app */}
        {hostRole !== 'admin' && <PendingDeletionScreen />}
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<FullPageSpinner />}>
            <Routes>
              {/* Called as plain functions, not JSX components — <Routes>
                  walks its direct children looking for <Route>/<Fragment>
                  elements specifically; a custom component wrapping them
                  (<AdminRoutes />) is NOT recognized the same way and
                  silently fails to match anything. Calling the function
                  directly inlines its returned Route/Fragment tree exactly
                  as if it had been written here literally. */}
              {hostRole === 'admin' ? AdminRoutes() : LearnerRoutes()}
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
    </ControlsProvider>
    </ThemeProvider>
  );
}
